import React, {Fragment, useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import Button from "@material-ui/core/Button";
import {WEB_FAQ_LIST} from "../../../../../../constants/appUrls.constants";
import {translateFaq, translateFaqClearState} from "../../../../../../redux/actions/Web/Faq/faqActions";
import {isDefined} from "../../../../../../utils";
import faqStyles from "./faqStyles";
import {sortByLanguageCode} from "./faqLanguageOrder";

const QUESTION_MAX_LENGTH = 255;
const ANSWER_MAX_LENGTH = 2000;

function TabPanel({children, value, index}) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`faq-lang-tabpanel-${index}`}
            aria-labelledby={`faq-lang-tab-${index}`}
        >
            {value === index && children}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `faq-lang-tab-${index}`,
        'aria-controls': `faq-lang-tabpanel-${index}`,
    };
}

const sortLanguages = (languages) => sortByLanguageCode(languages, lang => lang?.code);

const TranslateIcon = ({loading, muted}) => (
    loading
        ? <i style={{fontSize: 15, marginRight: 6}} className="fas fa-spinner-third fa-spin"/>
        : (
            <svg
                fill={muted ? "#c3cad6" : "#d71f26"}
                style={{width: "1.1rem", height: "1rem", marginRight: 6}}
                viewBox="0 0 52 52"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M39,18.67H35.42l-4.2,11.12A29,29,0,0,1,20.6,24.91a28.76,28.76,0,0,0,7.11-14.49h5.21a2,2,0,0,0,0-4H19.67V2a2,2,0,1,0-4,0V6.42H2.41a2,2,0,0,0,0,4H7.63a28.73,28.73,0,0,0,7.1,14.49A29.51,29.51,0,0,1,3.27,30a2,2,0,0,0,.43,4,1.61,1.61,0,0,0,.44-.05,32.56,32.56,0,0,0,13.53-6.25,32,32,0,0,0,12.13,5.9L22.83,52H28l2.7-7.76H43.64L46.37,52h5.22Zm-15.3-8.25a23.76,23.76,0,0,1-6,11.86,23.71,23.71,0,0,1-6-11.86Zm8.68,29.15,4.83-13.83L42,39.57Z"/>
            </svg>
        )
);

// Create / Edit sayfalarinin ortak cok dilli SSS formu.
// faq[] semasi: [{ language_code, question, answer }] — sorusu bos olan dil gonderilmez.
const FaqForm = ({initialLanguages, initialAction, saving, errorMessage, onSubmit}) => {
    const {t} = useTranslation();
    const classes = faqStyles();
    const dispatch = useDispatch();

    const languageList = useSelector(state => state.languagesReducer?.languages);
    const translateFetching = useSelector(state => state.faqReducers?.translateFetching);
    const translateData = useSelector(state => state.faqReducers?.translateData);
    const translateError = useSelector(state => state.faqReducers?.translateError);

    const [formData, setFormData] = useState({});
    const [langTabValue, setLangTabValue] = useState(0);
    const [action, setAction] = useState(initialAction ? initialAction : "publish");
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});

    const languages = sortLanguages(languageList);

    useEffect(() => {
        setAction(initialAction ? initialAction : "publish");
    }, [initialAction]);

    useEffect(() => {
        const currentLanguages = sortLanguages(languageList);
        if (currentLanguages.length === 0) return;

        const newFormData = {};
        currentLanguages.forEach(lang => {
            if (!isDefined(lang?.code)) return;
            const item = Array.isArray(initialLanguages)
                ? initialLanguages.find(faqLang => faqLang?.language_code === lang.code)
                : null;
            newFormData[lang.code] = {
                question: isDefined(item?.question) ? item.question : '',
                answer: isDefined(item?.answer) ? item.answer : '',
            };
        });
        setFormData(newFormData);
    }, [initialLanguages, languageList]);

    useEffect(() => {
        if (!isDefined(translateData)) return;

        const translated = Array.isArray(translateData?.faq) ? translateData.faq : [];
        if (translated.length > 0) {
            setFormData(prev => {
                const updated = {...prev};
                translated.forEach(item => {
                    const code = item?.language_code;
                    if (!isDefined(code) || !isDefined(updated[code])) return;
                    updated[code] = {
                        question: isDefined(item?.question) ? item.question : updated[code].question,
                        answer: isDefined(item?.answer) ? item.answer : updated[code].answer,
                    };
                });
                return updated;
            });
            setSnackbar({open: true, message: t('enw-translated_to_all_languages'), severity: 'success'});
        } else {
            setSnackbar({open: true, message: t('enw-error-message'), severity: 'error'});
        }
        dispatch(translateFaqClearState());
    }, [translateData, dispatch, t]);

    useEffect(() => {
        if (translateError) {
            setSnackbar({open: true, message: t('enw-error-message'), severity: 'error'});
            dispatch(translateFaqClearState());
        }
    }, [translateError, dispatch, t]);

    const handleChange = (langCode, field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [langCode]: {
                ...prev[langCode],
                [field]: value,
            }
        }));
    };

    const handleTranslate = (langCode) => () => {
        const current = formData[langCode];
        if (!isDefined(current?.question)) return;

        dispatch(translateFaq({
            source_language_code: langCode,
            question: current.question,
            answer: current.answer,
            languages: languages,
        }));
    };

    const handleSubmit = () => {
        const faq = languages
            .filter(lang => isDefined(formData[lang?.code]?.question) && formData[lang.code].question.trim() !== '')
            .map(lang => ({
                language_code: lang.code,
                question: formData[lang.code].question,
                answer: formData[lang.code].answer ? formData[lang.code].answer : null,
            }));

        if (faq.length === 0) return;

        onSubmit({faq, action});
    };

    const hasQuestion = (code) => isDefined(formData[code]?.question) && formData[code].question.trim() !== '';

    const filledCount = languages.filter(lang => hasQuestion(lang?.code)).length;
    const progress = languages.length > 0 ? Math.round((filledCount / languages.length) * 100) : 0;
    const canSave = !saving && !translateFetching && filledCount > 0;

    return (
        <Fragment>
            <style>{`
                .faq-q-input {margin-top: 10px!important;}
            `}</style>
            <Box className={classes.panel}>
                <Box className={classes.panelHeader}>
                    <Box className={classes.panelTitle}>
                        <i className="fas fa-question-circle" style={{color: '#d71f26'}}/>
                        {t('enw-web-faq-question_answer', 'Soru ve Cevap')}
                    </Box>

                    <Box className={classes.progressWrap}>
                        <Box className={classes.progressBar}>
                            <Box className={classes.progressFill} style={{width: `${progress}%`}}/>
                        </Box>
                        <Typography className={classes.progressText}>
                            {`${filledCount}/${languages.length} ${t('enw-web-faq-language_progress', 'dil dolduruldu')}`}
                        </Typography>
                    </Box>

                    <TextField
                        select
                        variant="outlined"
                        size="small"
                        className={classes.statusSelect}
                        label={t('enw-input-status')}
                        value={action}
                        onChange={(event) => setAction(event.target.value)}
                    >
                        <MenuItem value="publish">
                            <i className="fas fa-globe" style={{color: '#1e7e34', marginRight: 8, fontSize: 12}}/>
                            {t('enw-web-faq-status_publish', 'Yayında')}
                        </MenuItem>
                        <MenuItem value="preview">
                            <i className="fas fa-eye" style={{color: '#b26a00', marginRight: 8, fontSize: 12}}/>
                            {t('enw-web-faq-status_preview', 'Önizleme')}
                        </MenuItem>
                    </TextField>
                </Box>

                {errorMessage &&
                    <Box style={{padding: '14px 22px 0'}}>
                        <Alert severity="error" style={{borderRadius: 10}}>{errorMessage}</Alert>
                    </Box>
                }

                {languages.length === 0 ?
                    <Box className={classes.loadingBoxInline}>
                        <CircularProgress className={classes.spinner} style={{color: '#d71f26'}}/>
                    </Box>
                    :
                    <Fragment>
                        <AppBar position="static" className={classes.langTabsBar} elevation={0}>
                            <Tabs
                                value={langTabValue}
                                onChange={(event, newValue) => setLangTabValue(newValue)}
                                aria-label="faq-lang-tabs"
                                variant="scrollable"
                                scrollButtons="on"
                                classes={{indicator: classes.langTabIndicator}}
                            >
                                {languages.map((lang, index) => (
                                    <Tab
                                        key={lang?.code || index}
                                        className={classes.langTab}
                                        label={
                                            <Box display="flex" alignItems="center" style={{color: "#000000de"}}>
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/assets/img/language/${lang?.code || ''}.svg`}
                                                    alt={lang?.code || ''}
                                                    className={classes.flagImg}
                                                />
                                                {lang?.code?.toUpperCase() || ''}
                                                {hasQuestion(lang?.code) &&
                                                    <i
                                                        className="fas fa-check-circle"
                                                        style={{color: '#1e7e34', fontSize: 11, marginLeft: 6}}
                                                    />
                                                }
                                            </Box>
                                        }
                                        {...a11yProps(index)}
                                    />
                                ))}
                            </Tabs>
                        </AppBar>

                        {languages.map((lang, index) => {
                            const question = isDefined(formData[lang?.code]?.question) ? formData[lang.code].question : '';
                            const answer = isDefined(formData[lang?.code]?.answer) ? formData[lang.code].answer : '';
                            const canTranslate = question.trim() !== '' && !translateFetching && !saving;

                            return (
                                <TabPanel value={langTabValue} index={index} key={lang?.code || index}>
                                    <Box className={classes.tabPanelContent}>
                                        <Box className={classes.fieldRow}>
                                            <Box display="flex" justifyContent="flex-end" style={{ marginBottom: 0 }}>
                                                <Tooltip
                                                    title={t('enw-web-faq-translate_tooltip', 'Bu dildeki soru ve cevabı tüm dillere çevirir')}
                                                    placement="top"
                                                >
                                                    {/* MUI'nin disabled state'i kullanilmiyor: global
                                                        `button.Mui-disabled { color:#fff !important }` kurali
                                                        yaziyi gorunmez yapiyor. Pasif gorunum inline verilir. */}
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={canTranslate ? handleTranslate(lang?.code) : undefined}
                                                        style={{
                                                            borderRadius: 10,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: 13,
                                                            padding: '5px 14px',
                                                            background: '#fff',
                                                            border: '1px solid #eaedf3',
                                                            color: canTranslate ? '#2d3748' : '#a0aec0',
                                                            cursor: canTranslate ? 'pointer' : 'not-allowed',
                                                        }}
                                                    >
                                                        <TranslateIcon loading={translateFetching} muted={!canTranslate}/>
                                                        {t('btn-translate')}
                                                    </Button>
                                                </Tooltip>
                                            </Box>

                                            <TextField
                                                fullWidth
                                                autoFocus
                                                variant="outlined"
                                                className={classes.inputField + " faq-q-input"}
                                                label={`${t('enw-web-faq-question', 'Soru')} — ${lang?.code?.toUpperCase() || ''}`}
                                                placeholder={t('enw-web-faq-question_placeholder', 'Örn: Giriş ve çıkış saatleri nedir?')}
                                                value={question}
                                                onChange={handleChange(lang?.code, 'question')}
                                                inputProps={{maxLength: QUESTION_MAX_LENGTH}}
                                            />
                                            <Box className={classes.fieldFooter} style={{justifyContent: 'flex-end'}}>
                                                <Typography className={classes.charCounter}>
                                                    {question.length} / {QUESTION_MAX_LENGTH}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box className={classes.fieldRow}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={6}
                                                variant="outlined"
                                                className={classes.inputField}
                                                label={`${t('enw-web-faq-answer', 'Cevap')} — ${lang?.code?.toUpperCase() || ''}`}
                                                placeholder={t('enw-web-faq-answer_placeholder', 'Misafirin aradığı bilgiyi net ve kısa şekilde yazın.')}
                                                value={answer}
                                                onChange={handleChange(lang?.code, 'answer')}
                                                inputProps={{maxLength: ANSWER_MAX_LENGTH}}
                                            />
                                            <Box className={classes.fieldFooter}>
                                                <Typography className={classes.fieldHint}>
                                                    <i className="fas fa-info-circle" style={{color: '#cbd2dd'}}/>
                                                    {t('enw-web-faq-language_hint', 'Sorusu boş bırakılan dil kaydedilmez.')}
                                                </Typography>
                                                <Typography className={classes.charCounter}>
                                                    {answer.length} / {ANSWER_MAX_LENGTH}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {question.trim() !== '' &&
                                        <Box className={classes.previewCard}>
                                            <Typography className={classes.previewLabel} component="div">
                                                <i className="fas fa-desktop"/>
                                                {t('enw-web-faq-preview', 'Sitede nasıl görünecek')}
                                            </Typography>
                                            <Typography className={classes.previewQuestion} component="div">
                                                <i className="fas fa-chevron-down" style={{color: '#d71f26', fontSize: 11, marginTop: 4}}/>
                                                {question}
                                            </Typography>
                                            {answer.trim() !== '' &&
                                                <Typography className={classes.previewAnswer} component="div">
                                                    {answer}
                                                </Typography>
                                            }
                                        </Box>
                                    }
                                </TabPanel>
                            );
                        })}

                        <Box className={classes.saveArea}>
                            <Link to={WEB_FAQ_LIST} className={classes.cancelBtn}>
                                {t('btn-back')}
                            </Link>
                            {/* Kaydet: MUI'nin disabled state'i kullanilmiyor (global
                                `button.Mui-disabled` kurallari yaziyi/zemini boziyor). Pasif ve
                                kaydediliyor gorunumleri inline verilir; metin her durumda kalir
                                ki buton genisligi degismesin. */}
                            <Button
                                onClick={canSave ? handleSubmit : undefined}
                                style={{
                                    background: canSave || saving
                                        ? 'linear-gradient(135deg, #d71f26 0%, #e8484f 100%)'
                                        : '#e9b6b8',
                                    color: '#fff',
                                    borderRadius: 10,
                                    padding: '9px 30px',
                                    minWidth: 160,
                                    minHeight: 44,
                                    fontWeight: 700,
                                    fontSize: 14,
                                    textTransform: 'none',
                                    boxShadow: canSave ? '0 4px 14px rgba(215, 31, 38, 0.28)' : 'none',
                                    cursor: canSave ? 'pointer' : 'not-allowed',
                                }}
                            >
                                {saving
                                    ? <CircularProgress className={classes.spinner} size={18} thickness={5} style={{color: '#fff', marginRight: 10}}/>
                                    : <i className="fas fa-check" style={{marginRight: 8, fontSize: 13}}/>
                                }
                                {t('btn-save')}
                            </Button>
                        </Box>
                    </Fragment>
                }
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({...prev, open: false}))}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({...prev, open: false}))}
                    severity={snackbar.severity}
                    variant="filled"
                    style={{borderRadius: 10}}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Fragment>
    );
};

export default FaqForm;
